<template>
  <div data-app>
    <div
      role="alert"
      v-bind:class="{ show: errors.length }"
      class="alert fade alert-danger"
    >
      <div class="alert-text" v-for="(error, i) in errors" :key="i">
        {{ error }}
      </div>
    </div>
    <v-card>
      <v-card-title>TLS Certificates</v-card-title>
      <v-data-table
        v-model="selected"
        :loading="loading"
        :headers="headers"
        :items="tls_certs"
        :search="search"
        :footer-props="{
          itemsPerPageOptions: [5, 10, 50, 100, 250, -1],
          showFirstLastPage: false,
          firstIcon: 'mdi-arrow-collapse-left',
          lastIcon: 'mdi-arrow-collapse-right',
          prevIcon: 'mdi-chevron-left',
          nextIcon: 'mdi-chevron-right',
        }"
        item-key="id"
        class="elevation-1"
      >
        <template v-slot:top>
          <v-toolbar flat color="white">
            <v-text-field
              v-model="search"
              append-icon="mdi-magnify"
              label="Search"
              single-line
              hide-details
            ></v-text-field>
            <v-spacer></v-spacer>
            <v-dialog v-model="dialog" max-width="600px">
              <template v-slot:activator="{ on }">
                <v-btn
                  color="primary"
                  dark
                  class="mb-2"
                  v-on="on"
                  @click="newItem"
                >
                  New Certificate
                </v-btn>
              </template>
              <v-card>
                <v-card-title>
                  <span class="headline">{{ formTitle }}</span>
                </v-card-title>

                <v-card-text>
                  <v-form ref="newForm" lazy-validation>
                    <v-container>
                      <v-row v-if="editedIndex === -1" dense>
                        <v-col>
                          <v-radio-group
                            v-model="editedItem.mode"
                            label="Mode"
                            :rules="requiredValue"
                            row
                          >
                            <v-radio label="Managed" value="managed"></v-radio>
                            <v-radio label="Manual" value="manual"></v-radio>
                          </v-radio-group>
                        </v-col>
                      </v-row>
                      <v-row v-if="editedItem.mode === 'managed'" dense>
                        <v-col>
                          <v-select
                            v-model="editedItem.website"
                            :items="websites"
                            return-object
                            item-text="display"
                            label="Website"
                            :rules="websiteRules"
                          ></v-select>
                          <div
                            v-if="websites.length === 0"
                            class="alert alert-warning"
                            role="alert"
                          >
                            <div class="alert-icon">
                              <v-icon small>mdi-alert</v-icon>
                            </div>
                            <div class="alert-text">No websites found.</div>
                          </div>
                          <div
                            v-if="selectedWebsiteHasWildcard"
                            class="alert alert-warning"
                            role="alert"
                          >
                            <div class="alert-icon alert-icon-top">
                              <v-icon small>mdi-alert</v-icon>
                            </div>
                            <div class="alert-text">
                              This website includes a wildcard name. Ensure you either
                              update the certificate to explicitly include each
                              additional subdomain when it is created, or consider
                              using DNS-01 for wildcard TLS certificate support
                            </div>
                          </div>
                        </v-col>
                      </v-row>
                      <v-row
                        v-if="
                          editedIndex === -1 &&
                          editedItem.mode === 'managed' &&
                          editedItem.website
                        "
                        dense
                      >
                        <v-col>
                          <v-select
                            v-model="editedItem.auth_type"
                            :items="authTypes"
                            item-text="label"
                            item-value="value"
                            label="Validation Method"
                            :rules="requiredValue"
                          ></v-select>
                          <div
                            v-if="editedItem.auth_type === 'http01'"
                            :class="
                              editedItem.website.has_h1
                                ? 'alert alert-success'
                                : 'alert alert-warning'
                            "
                            role="alert"
                          >
                            <div class="alert-icon">
                              <v-icon v-if="editedItem.website.has_h1" small>
                                mdi-check-circle
                              </v-icon>
                              <v-icon v-else small>mdi-alert</v-icon>
                            </div>
                            <div class="alert-text">
                              <span v-if="editedItem.website.has_h1">
                                This website has the required HTTP listener on
                                port 80.
                              </span>
                              <span v-else>
                                Create an HTTP listener on port 80 before
                                requesting this certificate.
                              </span>
                            </div>
                          </div>
                          <div
                            v-if="
                              editedItem.auth_type === 'dns01' &&
                              managedZonesLoading
                            "
                            class="alert alert-info"
                            role="alert"
                          >
                            <div class="alert-icon">
                              <v-icon small>mdi-information</v-icon>
                            </div>
                            <div class="alert-text">
                              Checking managed DNS zones...
                            </div>
                          </div>
                          <div
                            v-else-if="
                              editedItem.auth_type === 'dns01' &&
                              allSelectedDomainsManaged
                            "
                            class="alert alert-success"
                            role="alert"
                          >
                            <div class="alert-icon">
                              <v-icon small>mdi-check-circle</v-icon>
                            </div>
                            <div class="alert-text">
                              The selected website's domain is managed through
                              Path DNS. No further action is required.
                            </div>
                          </div>
                          <div
                            v-else-if="
                              editedItem.auth_type === 'dns01' &&
                              managedZonesLoaded
                            "
                            class="alert alert-warning"
                            role="alert"
                          >
                            <div class="alert-icon alert-icon-top">
                              <v-icon small>mdi-alert</v-icon>
                            </div>
                            <div class="alert-text">
                              <p>
                                This domain is not managed through Path DNS.
                                Ensure the following CNAME delegation records
                                are configured:
                              </p>
                              <ul class="mb-0 pl-4">
                                <li
                                  v-for="record in dnsDelegationRecords"
                                  :key="record.name"
                                >
                                  <code class="text-break">
                                    {{ record.name }} CNAME {{ record.target }}
                                  </code>
                                </li>
                              </ul>
                            </div>
                          </div>
                          <div
                            v-else-if="editedItem.auth_type === 'dns01'"
                            class="alert alert-danger"
                            role="alert"
                          >
                            <div class="alert-icon alert-icon-top">
                              <v-icon small>mdi-alert-circle</v-icon>
                            </div>
                            <div class="alert-text">
                              Managed DNS zones could not be checked. Try again
                              before requesting this certificate.
                            </div>
                          </div>
                        </v-col>
                      </v-row>
                      <v-row v-if="editedItem.mode === 'manual'" dense>
                        <v-col>
                          <v-file-input
                            v-model="fullChainFile"
                            label="Full Chain"
                            :rules="required"
                            required
                            accept=".crt, .pem"
                            @change="handleFullChainInput"
                          ></v-file-input>
                        </v-col>
                      </v-row>
                      <v-row v-if="editedItem.mode === 'manual'" dense>
                        <v-col>
                          <v-file-input
                            v-model="privKeyFile"
                            label="Private Key"
                            :rules="required"
                            required
                            accept=".key, .pem"
                            @change="handlePrivKeyInput"
                          ></v-file-input>
                        </v-col>
                      </v-row>
                    </v-container>
                  </v-form>
                </v-card-text>

                <v-card-actions>
                  <v-spacer></v-spacer>
                  <v-btn color="blue darken-1" text @click="close">
                    Cancel
                  </v-btn>
                  <v-btn color="blue darken-1" text @click="save">Save</v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>
          </v-toolbar>
        </template>
        <template v-slot:item.display_domain="{ item }">
          {{ item.display_domain }}
        </template>
        <template v-slot:item.display_names="{ item }">
          <span class="tls-cert-names" :title="item.full_names">
            {{ item.display_names }}
          </span>
        </template>
        <template v-slot:item.mode="{ item }">
          <v-tooltip v-if="item.mode === 'manual'" bottom>
            <template v-slot:activator="{ on }">
              <v-chip small v-on="on">
                {{ formatStatus(item.mode) }}
              </v-chip>
            </template>
            <span>Manual certificates must be replaced before expiry</span>
          </v-tooltip>
          <v-chip v-else-if="item.mode" small>
            {{ formatStatus(item.mode) }}
          </v-chip>
        </template>
        <template v-slot:item.auth_type="{ item }">
          {{ formatAuthType(item) }}
        </template>
        <template v-slot:item.status="{ item }">
          <v-tooltip v-if="item.status" bottom>
            <template v-slot:activator="{ on }">
              <v-chip
                small
                dark
                :color="statusColor(item.status)"
                v-on="statusChipListeners(item, on)"
              >
                {{ formatStatus(item.status) }}
              </v-chip>
            </template>
            <span>{{ statusHint(item) }}</span>
          </v-tooltip>
        </template>
        <template v-slot:item.not_after="{ item }">
          {{ formatDate(item.not_after) }}
        </template>
        <template v-slot:item.actions="{ item }">
          <v-tooltip bottom>
            <template v-slot:activator="{ on }">
              <v-icon small class="mr-2" @click="editItem(item)" v-on="on">
                mdi-pencil
              </v-icon>
            </template>
            <span>{{ item.mode === "manual" ? "Replace" : "Edit" }}</span>
          </v-tooltip>
          <v-tooltip v-if="canRetry(item)" bottom>
            <template v-slot:activator="{ on }">
              <v-icon small class="mr-2" @click="retryItem(item)" v-on="on">
                mdi-refresh
              </v-icon>
            </template>
            <span>Retry</span>
          </v-tooltip>
        </template>
      </v-data-table>
    </v-card>
    <v-dialog v-model="statusErrorDialog" max-width="600px">
      <v-card>
        <v-card-title>
          <span class="headline">{{ statusErrorTitle }}</span>
        </v-card-title>
        <v-card-text>
          <div v-for="(line, i) in statusErrorLines" :key="i">
            {{ line }}
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="blue darken-1" text @click="closeStatusError">
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import api from "@/common/api.service";
import Errors from "@/common/errors";

export default {
  mounted() {
    this.fetchTlsCerts();
    this.fetchWebsites();
    this.fetchManagedZones();
  },

  data: () => ({
    selected: [],
    headers: [
      { text: "ID", sortable: false, align: "left", value: "id" },
      { text: "Domain", value: "display_domain" },
      { text: "Names", value: "display_names" },
      { text: "Mode", value: "mode" },
      { text: "Validation", value: "auth_type" },
      { text: "Status", value: "status" },
      { text: "Expires", value: "not_after" },
      { text: "Actions", value: "actions", sortable: false },
    ],
    tls_certs: [],
    websites: [],
    managedZoneNames: [],
    managedZonesLoading: true,
    managedZonesLoaded: false,
    authTypes: [
      {
        label: "HTTP-01",
        value: "http01",
      },
      {
        label: "DNS-01",
        value: "dns01",
      },
    ],
    errors: [],
    loading: true,
    dialog: false,
    statusErrorDialog: false,
    statusErrorItem: null,
    editedIndex: -1,
    editedItem: {
      id: "",
      mode: null,
      auth_type: null,
      website: null,
      full_chain: "",
      priv_key: "",
    },
    defaultItem: {
      id: "",
      mode: null,
      auth_type: null,
      website: null,
      full_chain: "",
      priv_key: "",
    },
    fullChainFile: null,
    privKeyFile: null,
    search: "",
    required: [(v) => !!v || "File is required"],
    requiredValue: [(v) => !!v || "Value is required"],
  }),

  watch: {
    dialog(val) {
      val || this.close();
    },
  },

  methods: {
    fetchTlsCerts() {
      this.loading = true;
      api
        .query("waf/tls_certs")
        .then((response) => {
          this.tls_certs = response.data.tls_certs.map((cert) =>
            this.decorateTlsCert(cert)
          );
        })
        .catch((error) =>
          this.errors.push(...Errors.extractErrorMessage(error))
        )
        .finally(() => (this.loading = false));
    },

    fetchWebsites() {
      api
        .query("beta/waf/websites")
        .then((response) => {
          this.websites = response.data.websites.map((website) => {
            const hasH1Listener = website.listeners.some(
              (listener) => listener.protocol === "h1"
            );
            return {
              id: website.id,
              server_names: website.server_names,
              display: `${website.id} - ${website.server_names.join(", ")}`,
              has_h1: hasH1Listener,
            };
          });
        })
        .catch((error) =>
          this.errors.push(...Errors.extractErrorMessage(error))
        );
    },

    fetchManagedZones() {
      this.managedZonesLoading = true;
      this.managedZonesLoaded = false;
      api
        .query("zones")
        .then((response) => {
          this.managedZoneNames = response.data.zones.map((zone) => zone.name);
          this.managedZonesLoaded = true;
        })
        .catch((error) =>
          this.errors.push(...Errors.extractErrorMessage(error))
        )
        .finally(() => (this.managedZonesLoading = false));
    },

    newItem() {
      this.editedIndex = -1;
      this.editedItem = Object.assign({}, this.defaultItem);
      this.fullChainFile = null;
      this.privKeyFile = null;
    },

    editItem(item) {
      this.editedIndex = this.tls_certs.indexOf(item);
      this.editedItem = Object.assign({}, this.defaultItem, item, {
        website: this.findWebsiteForNames(item.names),
        full_chain: "",
        priv_key: "",
      });
      this.dialog = true;
    },

    close() {
      this.dialog = false;
      setTimeout(() => {
        this.editedItem = Object.assign({}, this.defaultItem);
        this.fullChainFile = null;
        this.privKeyFile = null;
        this.editedIndex = -1;
      }, 300);
    },

    save() {
      if (!this.$refs.newForm.validate()) return;

      this.errors = [];

      if (this.editedItem.mode === "managed") {
        this.saveManaged();
      } else {
        this.saveManual();
      }
    },

    saveManaged() {
      const names = this.editedItem.website.server_names;
      const request =
        this.editedIndex > -1
          ? api.patch(`waf/tls_certs/${this.editedItem.id}`, { names })
          : api.post("waf/tls_certs", {
              mode: "managed",
              names,
              auth_type: this.editedItem.auth_type,
            });

      request
        .then((response) => {
          this.upsertTlsCert(response.data);
          this.close();
        })
        .catch((error) =>
          this.errors.push(...Errors.extractErrorMessage(error))
        );
    },

    saveManual() {
      const cert = {
        full_chain: this.editedItem.full_chain,
        priv_key: this.editedItem.priv_key,
      };
      const request =
        this.editedIndex > -1
          ? api.put(`waf/tls_certs/${this.editedItem.id}`, cert)
          : api.post("waf/tls_certs", Object.assign({ mode: "manual" }, cert));

      request
        .then((response) => {
          this.upsertTlsCert(response.data);
          this.close();
        })
        .catch((error) =>
          this.errors.push(...Errors.extractErrorMessage(error))
        );
    },

    retryItem(item) {
      this.errors = [];
      api
        .post(`waf/tls_certs/${item.id}/retry`)
        .then((response) => this.upsertTlsCert(response.data))
        .catch((error) =>
          this.errors.push(...Errors.extractErrorMessage(error))
        );
    },

    upsertTlsCert(cert) {
      const decoratedCert = this.decorateTlsCert(cert);
      const index = this.tls_certs.findIndex(
        (item) => item.id === decoratedCert.id
      );
      if (index > -1) {
        this.tls_certs.splice(index, 1, decoratedCert);
      } else {
        this.tls_certs = [decoratedCert, ...this.tls_certs];
      }
    },

    decorateTlsCert(cert) {
      const displayDomain = this.formatDomain(cert.names);
      return Object.assign({}, cert, {
        display_domain: displayDomain,
        display_names: this.formatRelativeNames(cert.names, displayDomain),
        full_names: this.formatNames(cert.names),
      });
    },

    canRetry(item) {
      return ["failed", "renewal_degraded"].includes(item.status);
    },

    hasStatusError(item) {
      return this.canRetry(item) && !!item.error;
    },

    showStatusError(item, onlyWithError = false) {
      if (onlyWithError && !this.hasStatusError(item)) return;
      this.statusErrorItem = item;
      this.statusErrorDialog = true;
    },

    statusChipListeners(item, tooltipListeners) {
      const listeners = Object.assign({}, tooltipListeners);
      if (this.hasStatusError(item)) {
        listeners.click = () => this.showStatusError(item);
      }
      return listeners;
    },

    statusHint(item) {
      const hints = {
        active: "The certificate is issued and currently valid",
        pending:
          "The certificate has been requested and is waiting to be issued",
        failed:
          "Certificate issuance failed",
        degraded:
          "The certificate is valid, but renewal is failing",
        expired: "The certificate is expired",
      };
      const hint = hints[item.status] || "Certificate status";
      return this.hasStatusError(item) ? `${hint}. Click to view details` : hint;
    },

    closeStatusError() {
      this.statusErrorDialog = false;
      this.statusErrorItem = null;
    },

    findWebsiteForNames(names) {
      return (
        this.websites.find((website) =>
          this.sameNames(website.server_names, names)
        ) || null
      );
    },

    sameNames(firstNames, secondNames) {
      const first = [...(firstNames || [])].sort().join("\n");
      const second = [...(secondNames || [])].sort().join("\n");
      return first === second;
    },

    normalizeDomainName(name) {
      return (name || "").toLowerCase().replace(/\.+$/, "");
    },

    isDomainManaged(name) {
      const domain = this.normalizeDomainName(name);
      return this.managedZoneNames.some((zoneName) => {
        const zone = this.normalizeDomainName(zoneName);
        return domain === zone || domain.endsWith(`.${zone}`);
      });
    },

    formatNames(names) {
      return (names || []).join(", ");
    },

    formatDomain(names) {
      const normalizedNames = this.normalizedNames(names);
      if (normalizedNames.length === 0) return "";
      if (normalizedNames.length === 1) return normalizedNames[0];

      const splitNames = normalizedNames.map((name) => name.split("."));
      const commonLabels = [];
      let labelIndex = 1;
      while (
        splitNames.every(
          (labels) =>
            labels.length >= labelIndex &&
            labels[labels.length - labelIndex] ===
              splitNames[0][splitNames[0].length - labelIndex]
        )
      ) {
        commonLabels.unshift(splitNames[0][splitNames[0].length - labelIndex]);
        labelIndex += 1;
      }

      if (commonLabels.length >= 2) return commonLabels.join(".");
      return normalizedNames[0];
    },

    formatRelativeNames(names, domain) {
      if (!domain) return this.formatNames(names);
      return (names || [])
        .map((name) => this.formatRelativeName(name, domain))
        .join(", ");
    },

    formatRelativeName(name, domain) {
      const wildcard = name.startsWith("*.");
      const cleanName = wildcard ? name.slice(2) : name;
      if (cleanName === domain) return wildcard ? "*" : "@";

      const domainSuffix = `.${domain}`;
      if (!cleanName.endsWith(domainSuffix)) return name;

      const relativeName = cleanName.slice(0, -domainSuffix.length);
      return wildcard ? `*.${relativeName}` : relativeName;
    },

    normalizedNames(names) {
      return (names || [])
        .map((name) => name.toLowerCase().replace(/^\*\./, ""))
        .filter((name) => !!name);
    },

    formatStatus(status) {
      return status ? status.replace(/_/g, " ") : "";
    },

    formatAuthType(item) {
      if (item.mode !== "managed") return "N/A";
      return (
        {
          http01: "HTTP-01",
          dns01: "DNS-01",
        }[item.auth_type] || "N/A"
      );
    },

    formatDate(value) {
      if (!value) return "";
      return new Date(value).toISOString();
    },

    statusColor(status) {
      const colors = {
        active: "green",
        pending: "blue",
        failed: "red",
        renewal_degraded: "orange",
        expired: "grey",
      };
      return colors[status] || "grey";
    },

    handlePrivKeyInput(file) {
      if (!file) {
        this.editedItem.priv_key = "";
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        this.editedItem.priv_key = e.target.result;
      };
      reader.readAsText(file);
    },

    handleFullChainInput(file) {
      if (!file) {
        this.editedItem.full_chain = "";
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        this.editedItem.full_chain = e.target.result;
      };
      reader.readAsText(file);
    },
  },

  computed: {
    dnsDelegationRecords() {
      const website = this.editedItem.website;
      if (!website || !website.server_names) return [];

      const bases = [
        ...new Set(
          website.server_names.map((name) => name.replace(/^\*\./, ""))
        ),
      ];
      return bases
        .filter((base) => !this.isDomainManaged(base))
        .map((base) => ({
          name: `_acme-challenge.${base}`,
          target: `${base}.pathdomainvalidation.net`,
        }));
    },

    allSelectedDomainsManaged() {
      const website = this.editedItem.website;
      return (
        this.managedZonesLoaded &&
        !!website &&
        website.server_names.length > 0 &&
        this.dnsDelegationRecords.length === 0
      );
    },

    selectedWebsiteHasWildcard() {
      const website = this.editedItem.website;
      return (
        this.editedItem.mode === "managed" &&
        !!website &&
        website.server_names.some((name) => name.startsWith("*."))
      );
    },

    websiteRules() {
      return [
        (v) => (!!v && !!v.server_names) || "Website is required",
        (v) =>
          this.editedItem.auth_type !== "http01" ||
          (!!v && v.has_h1) ||
          "Website does not have an HTTP/1 (non-HTTPS) listener. Please configure one on port 80, this is required for a certificate request to succeed",
      ];
    },

    formTitle() {
      if (this.editedIndex === -1) {
        if (this.editedItem.mode === "managed") {
          return "New Managed Certificate";
        }
        if (this.editedItem.mode === "manual") return "Upload Certificate";
        return "New Certificate";
      }
      return this.editedItem.mode === "manual"
        ? "Replace Certificate"
        : "Edit Managed Certificate";
    },

    statusErrorTitle() {
      if (!this.statusErrorItem) return "Certificate Status";
      const status = this.formatStatus(this.statusErrorItem.status);
      return `${status.charAt(0).toUpperCase()}${status.slice(1)} Certificate`;
    },

    statusErrorLines() {
      const error = this.statusErrorItem ? this.statusErrorItem.error : "";
      return error.split("\n");
    },
  },
};
</script>

<style scoped>
.tls-cert-names {
  display: inline-block;
  max-width: 320px;
  overflow: hidden;
  text-overflow: ellipsis;
  vertical-align: bottom;
  white-space: nowrap;
}

</style>
